import { motion } from "framer-motion";
import { Crane, Handshake, UsersThree } from "@phosphor-icons/react";
import { SectionHeader } from "@/components/SectionHeader";

const services = [
  { icon: Handshake, title: "Transaction Support", subtitle: "Commission Model", href: "/services#transaction-support", items: ["Intermediary payment processing", "Pre-payment services", "Post-payment services", "Transaction coordination", "Commercial support"], wide: true, testId: "service-transaction-card" },
  { icon: UsersThree, title: "Manpower Supply", subtitle: "Skilled + Support Teams", href: "/manpower", items: ["Mechanical and civil specialists", "Short-term and long-term projects", "Certified site-ready workforce"], testId: "service-manpower-card" },
  { icon: Crane, title: "Equipment Rental", subtitle: "Plant + Machinery", href: "/equipment", items: ["Cranes, manlifts and excavators", "DG sets and air compressors", "Experienced operators available"], testId: "service-equipment-card" },
];

const cardInitial = { opacity: 0, y: 34 };
const cardInView = { opacity: 1, y: 0 };
const viewportOnce = { once: true };

export function Services() {
  return (
    <section id="services" className="border-b border-industrial-line bg-[#F1F1EC] py-24 sm:py-32" data-testid="services-section">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
        <SectionHeader eyebrow="Services / 02" title="Three verticals. One accountable partner." description="A focused operating model for project teams that need people, machines, and commercial coordination without fragmented vendor management." testId="services-section-header" />
        <div className="grid gap-8 lg:grid-cols-3">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.a
                href={service.href}
                key={service.title}
                data-testid={service.testId}
                initial={cardInitial}
                whileInView={cardInView}
                viewport={viewportOnce}
                transition={{ delay: index * 0.12, duration: 0.7 }}
                className={`${service.wide ? "lg:col-span-1 lg:row-span-2" : ""} group relative flex min-h-[360px] flex-col justify-between overflow-hidden border border-industrial-line bg-industrial-surface p-7 transition-all hover:-translate-y-1 hover:translate-x-1 hover:hard-shadow-sm sm:p-8`}
              >
                <div className="absolute right-0 top-0 h-24 w-24 translate-x-12 -translate-y-12 rotate-45 bg-industrial-yellow/10 transition-colors group-hover:bg-industrial-yellow/25" />
                <div>
                  <Icon size={46} weight="fill" className="text-industrial-yellow" />
                  <p className="mt-8 font-mono text-xs uppercase tracking-[0.24em] text-industrial-muted">{service.subtitle}</p>
                  <h3 className="mt-3 font-heading text-3xl font-semibold uppercase leading-none text-industrial-text">{service.title}</h3>
                  <ul className="mt-8 space-y-3">
                    {service.items.map((item) => <li key={item} className="flex gap-3 text-sm text-industrial-muted"><span className="mt-2 h-1.5 w-1.5 shrink-0 bg-industrial-yellow" />{item}</li>)}
                  </ul>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-industrial-yellow">
                  View service <span aria-hidden="true" className="text-sm leading-none transition-transform group-hover:translate-x-1">↗</span>
                </span>
              </motion.a>
            );
          })}
          <motion.div initial={cardInitial} whileInView={cardInView} viewport={viewportOnce} transition={{ delay: 0.3 }} className="border border-industrial-yellow bg-industrial-yellow p-8 text-industrial-black lg:col-span-2" data-testid="services-commercial-banner">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.28em]">Commercial stance</p>
            <p className="mt-4 max-w-4xl font-heading text-3xl font-semibold uppercase leading-tight sm:text-4xl">Quality service, competitive terms, timely execution, and professional project support.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
